// src/lib/realtime/reauth.ts
// What a real-time connection does when the gateway ends it (contract §19, §6.10): 4401 → refresh, then reconnect; 1012 → reconnect with backoff.

import { refreshSession } from "../http/refresh";
import { isSignedIn, loadAuthSession } from "../http/session";

export const CLOSE_REAUTHENTICATE = 4401;
export const CLOSE_SERVICE_RESTART = 1012;

// reconnect: the session is good; signed_out: it ended (the session store already sends the user to login); retry_later: identity could not answer.
export type ReauthOutcome = "reconnect" | "signed_out" | "retry_later";

const reread = async (): Promise<ReauthOutcome> => {
	try {
		const info = await loadAuthSession();
		return info && isSignedIn() ? "reconnect" : "signed_out";
	} catch {
		// Logged in loadAuthSession; the cookie may still be good, so the caller backs off and tries again.
		return isSignedIn() ? "retry_later" : "signed_out";
	}
};

// After a 4401: rotate the cookies, then re-read GET /auth/session so a role or permission change reaches the UI gates as well.
export const reauthenticateAfterClose = async (): Promise<ReauthOutcome> => {
	if (!isSignedIn()) return "signed_out";
	const refreshed = await refreshSession();
	if (!isSignedIn()) return "signed_out";
	if (!refreshed) return "retry_later";
	return reread();
};

// After a drop with no usable close code: the browser cannot see an upgrade's 401, so ask identity first (the shared client refreshes on a 401).
export const confirmSessionBeforeReconnect = async (): Promise<ReauthOutcome> => {
	if (!isSignedIn()) return "signed_out";
	return reread();
};

// pusher-js reports a close as {data: {code}} and wraps it again as {error: {data: {code}}}; engine.io puts the CloseEvent in `context`.
export const closeCodeOf = (event: unknown): number | undefined => {
	const e = event as { code?: unknown; data?: { code?: unknown }; error?: { data?: { code?: unknown } }; context?: { code?: unknown } } | null | undefined;
	const code = e?.data?.code ?? e?.error?.data?.code ?? e?.context?.code ?? e?.code;
	return typeof code === "number" ? code : undefined;
};
